import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, Card, CardContent, Button, Dialog, DialogTitle, DialogContent, DialogActions, Chip, CircularProgress, Backdrop } from '@mui/material';

const CalendarPage: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewingAppointment, setViewingAppointment] = useState<any>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);

  useEffect(() => {
    fetchAppointments();
  }, [selectedDate]);

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://salon.rockyroadsolutions.com/api/appointments');
      if (response.ok) {
        const data = await response.json();
        // Fetch services for each appointment
        const appointmentsWithServices = await Promise.all(
          data.map(async (appointment: any) => {
            try {
              const servicesResponse = await fetch(`https://salon.rockyroadsolutions.com/api/appointments/${appointment.id}`);
              if (servicesResponse.ok) {
                const appointmentDetail = await servicesResponse.json();
                return { ...appointment, services: appointmentDetail.services };
              }
            } catch (error) {
              console.error('Error fetching services for appointment:', appointment.id);
            }
            return appointment;
          })
        );
        setAppointments(appointmentsWithServices);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date: Date) => {
    return date.toDateString() === selectedDate.toDateString();
  };

  const getAppointmentsForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments.filter(apt => {
      const aptDate = new Date(apt.appointmentDate).toISOString().split('T')[0];
      return aptDate === dateStr;
    });
  };

  const goToPreviousMonth = () => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const days = getDaysInMonth(selectedDate);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return (
      <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Calendar
      </Typography>
      
      <Grid container spacing={3} sx={{ height: 'calc(100vh - 200px)' }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Button onClick={goToPreviousMonth}>Previous</Button>
              <Typography variant="h5">
                {monthNames[selectedDate.getMonth()]} {selectedDate.getFullYear()}
              </Typography>
              <Button onClick={goToNextMonth}>Next</Button>
            </Box>
            
            <Box display="flex" justifyContent="center" mb={2}>
              <Button variant="outlined" onClick={goToToday}>
                Today
              </Button>
            </Box>

            <Grid container spacing={1}>
              {dayNames.map(day => (
                <Grid item xs={12/7} key={day}>
                  <Box textAlign="center" py={1} fontWeight="bold">
                    {day}
                  </Box>
                </Grid>
              ))}
              
              {days.map((day, index) => (
                <Grid item xs={12/7} key={index}>
                  <Box
                    sx={{
                      height: 80,
                      border: '1px solid #e0e0e0',
                      cursor: day ? 'pointer' : 'default',
                      backgroundColor: day && isSelected(day) ? '#1976d2' : day && isToday(day) ? '#f0f0f0' : 'white',
                      color: day && isSelected(day) ? 'white' : 'black',
                      display: 'flex',
                      flexDirection: 'column',
                      p: 0.5
                    }}
                    onClick={() => day && setSelectedDate(day)}
                  >
                    {day && (
                      <>
                        <Typography variant="body2" fontWeight={isToday(day) ? 'bold' : 'normal'}>
                          {day.getDate()}
                        </Typography>
                        {getAppointmentsForDate(day).length > 0 && (
                          <Typography variant="caption" sx={{ fontSize: '10px' }}>
                            {getAppointmentsForDate(day).length} apt
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={4} sx={{ height: '100%' }}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>
              Appointments for {selectedDate.toDateString()}
            </Typography>
            
            <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
              {getAppointmentsForDate(selectedDate).length === 0 ? (
                <Paper sx={{ p: 2 }}>
                  <Typography color="text.secondary">
                    No appointments scheduled for this date.
                  </Typography>
                </Paper>
              ) : (
                getAppointmentsForDate(selectedDate).map((appointment) => (
                  <Card 
                    key={appointment.id} 
                    sx={{ 
                      mb: 2,
                      background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                      border: '1px solid #E91E63',
                      borderRadius: 3,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': { 
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(233, 30, 99, 0.2)',
                        borderColor: '#AD1457'
                      }
                    }}
                    onClick={() => {
                      setViewingAppointment(appointment);
                      setIsViewDialogOpen(true);
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <Box sx={{
                          width: 35,
                          height: 35,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.9rem'
                        }}>
                          {appointment.client?.firstName?.[0]}{appointment.client?.lastName?.[0]}
                        </Box>
                        <Box flex={1}>
                          <Typography variant="body1" fontWeight={600} color="#E91E63">
                            🕐 {appointment.startTime} - {appointment.endTime}
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {appointment.client?.firstName} {appointment.client?.lastName}
                          </Typography>
                        </Box>
                        <Chip
                          label={appointment.status}
                          color={appointment.status === 'COMPLETED' ? 'success' : appointment.status === 'CANCELLED' ? 'error' : 'primary'}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          💅 with {appointment.primaryStaff?.firstName} {appointment.primaryStaff?.lastName}
                        </Typography>
                        <Box textAlign="right">
                          <Typography variant="h6" fontWeight={700} color="#9C27B0">
                            ${(parseFloat(appointment.totalPrice) + parseFloat(appointment.tipAmount || 0)).toFixed(2)}
                          </Typography>
                          {parseFloat(appointment.tipAmount || 0) > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Includes ${parseFloat(appointment.tipAmount).toFixed(2)} tip
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
      
      {/* Fancy Appointment Details Dialog */}
      <Dialog 
        open={isViewDialogOpen} 
        onClose={() => setIsViewDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
          color: 'white',
          textAlign: 'center',
          py: 2.5,
          boxShadow: '0 2px 8px rgba(233, 30, 99, 0.3)'
        }}>
          <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: '0.5px' }}>
            ✨ Appointment Details
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ mt: 2 }}>
            {/* Client & Staff Section */}
            <Card sx={{ mb: 2, border: '1px solid #E91E63', borderRadius: 2 }}>
              <CardContent sx={{ py: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Box sx={{ 
                        width: 35, 
                        height: 35, 
                        borderRadius: '50%', 
                        background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.9rem',
                        fontWeight: 'bold'
                      }}>
                        {viewingAppointment?.client?.firstName?.[0]}{viewingAppointment?.client?.lastName?.[0]}
                      </Box>
                      <Typography variant="subtitle1" fontWeight={600} color="#E91E63">Client</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>{viewingAppointment?.client?.firstName} {viewingAppointment?.client?.lastName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      <a href={`mailto:${viewingAppointment?.client?.email}`} style={{ color: '#1976d2', textDecoration: 'none' }}>
                        {viewingAppointment?.client?.email}
                      </a>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      📞 +1-234-567-8900 |
                      <a href={`tel:+12345678900`} style={{ color: '#4CAF50', textDecoration: 'none', marginLeft: '5px', marginRight: '10px' }}>
                        Call
                      </a>
                      <a href={`https://wa.me/12345678900`} target="_blank" rel="noopener noreferrer" style={{ color: '#25D366', textDecoration: 'none' }}>
                        WhatsApp
                      </a>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Box sx={{ 
                        width: 35, 
                        height: 35, 
                        borderRadius: '50%', 
                        background: 'linear-gradient(135deg, #9C27B0 0%, #6A1B9A 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.9rem',
                        fontWeight: 'bold'
                      }}>
                        {viewingAppointment?.primaryStaff?.firstName?.[0]}{viewingAppointment?.primaryStaff?.lastName?.[0]}
                      </Box>
                      <Typography variant="subtitle1" fontWeight={600} color="#9C27B0">Staff</Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={500}>{viewingAppointment?.primaryStaff?.firstName} {viewingAppointment?.primaryStaff?.lastName}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{viewingAppointment?.primaryStaff?.role}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>



            {/* Services Section */}
            <Card sx={{ mb: 2, border: '1px solid #4CAF50', borderRadius: 2 }}>
              <CardContent sx={{ py: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} color="#4CAF50" gutterBottom>💅 Services</Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {viewingAppointment?.services?.map((service: any, index: number) => (
                    <Chip
                      key={index}
                      label={`${service.serviceName || service.name} - $${service.price}`}
                      sx={{
                        background: 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)',
                        color: 'white',
                        fontWeight: 500
                      }}
                      size="small"
                    />
                  )) || (
                    <Typography variant="body2" color="text.secondary">
                      No services listed
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Schedule & Payment Section */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Card sx={{ mb: 2, border: '1px solid #FF5722', borderRadius: 2 }}>
                  <CardContent sx={{ py: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} color="#FF5722" gutterBottom>📅 Schedule</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">Date</Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {new Date(viewingAppointment?.appointmentDate).toLocaleDateString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">Time</Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {viewingAppointment?.startTime} - {viewingAppointment?.endTime}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">Duration</Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {viewingAppointment?.totalDuration} min
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ mb: 2, border: '1px solid #00BCD4', borderRadius: 2 }}>
                  <CardContent sx={{ py: 2, textAlign: 'center' }}>
                    <Typography variant="subtitle1" fontWeight={600} color="#00BCD4" gutterBottom>💰 Total</Typography>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Service: ${parseFloat(viewingAppointment?.totalPrice || 0).toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Tip: ${parseFloat(viewingAppointment?.tipAmount || 0).toFixed(2)}
                      </Typography>
                    </Box>
                    <Typography variant="h4" fontWeight={700} color="#00BCD4">
                      ${(parseFloat(viewingAppointment?.totalPrice || 0) + parseFloat(viewingAppointment?.tipAmount || 0)).toFixed(2)}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight={500}
                      sx={{
                        color: viewingAppointment?.status === 'COMPLETED' ? '#4CAF50' : 
                              viewingAppointment?.status === 'CANCELLED' ? '#F44336' : '#FF9800',
                        textTransform: 'capitalize'
                      }}
                    >
                      {viewingAppointment?.status?.toLowerCase()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {viewingAppointment?.notes && (
              <Card sx={{ border: '1px solid #9E9E9E', borderRadius: 2 }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} color="#666" gutterBottom>📝 Notes</Typography>
                  <Typography variant="body2">{viewingAppointment.notes}</Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button 
            onClick={() => setIsViewDialogOpen(false)}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #E91E63 0%, #AD1457 100%)',
              px: 3
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CalendarPage;
